import { GameService } from './../../../services/game-service';
import { EventAggregator, ICustomElementViewModel, inject } from 'aurelia';
import { LanguageWebsiteService } from '../../../services/language-website-service';
import { SessionService } from '../../../services/session-service';
import { websiteShortCode, environment } from '../../../environment';
import { Helper } from '../../extensions';
import './footer.scss';
import type { Language as LanguageItem } from '../../../state/models/ILanguage';
import type { LanguageCurrencyChangedDetail, Language } from '../../types/footerTypes';

const CURRENCY_KEY = 'currency';
const UNDEFINED_SHORTCODE = 'UNDEFINED SHORTCODE';
const DISCORD_INVITE_LINK = 'https://discord.com/invite/chicksgroup';

@inject(
    SessionService,
    EventAggregator,
    Helper,
    LanguageWebsiteService
)
export class Footer implements ICustomElementViewModel {
    viewingAsAdmin: boolean;
    adminViewSubscriber;
    shortCode: string = websiteShortCode() ?? 'CG';
    environmentLink: string;
    footerLogo: string;
    env: string = environment() ?? 'local';
    footerRef: HTMLDivElement;
    selectedLanguage: Language;

    constructor(
        private readonly sessionService: SessionService,
        private readonly eventAggregator: EventAggregator,
        private readonly helper: Helper,
        private readonly languageService: LanguageWebsiteService,
    ) {
        this.environmentLink = this.helper.environmentLink;
        this.footerLogo = this.helper.dynamicLogo;
    }

    async attached() {
        this.footerRef.addEventListener('languagecurrencychanged', this.handleComponentEvent);
        this.selectedLanguage = await this.sessionService.getLanguage();
    }

    detached() {
        this.adminViewSubscriber?.dispose();
        this.footerRef.removeEventListener('languagecurrencychanged', this.handleComponentEvent);
    }

    handleEventSubscriptions() {
        this.adminViewSubscriber = this.eventAggregator.subscribe('admin-view-updated', (payload: { bool: boolean }) => {
            this.viewingAsAdmin = payload.bool;
        });
    }

    get supportEmail() {
        switch (this.shortCode) {
            case 'DS':
                return 'support@divicasales.com';
            case 'CG':
                return 'support@chicksgold.com';
            case 'GT':
                return 'support@gamertotal.com';
            case 'CX':
                return 'support@chicksx.com';
            case 'AG':
                return 'support@aribagold.com';
            case 'AK':
                return 'support@acckings.com';
            default:
                return 'UNDEFINED SHORTCODE';
        }
    }

    get trustpilotLink() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.trustpilot.com/review/chicksgold.com';
            case 'CX':
                return 'https://www.trustpilot.com/review/chicksx.com';
            default:
                return UNDEFINED_SHORTCODE;
        }
    }

    get socialMediaTwitter() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://twitter.com/chicksgold';
            case 'CX':
                return 'https://twitter.com/ChicksXcrypto';
            case 'DS':
                return 'https://twitter.com/SalesDivica';
            case 'GT':
                return 'https://twitter.com/GamerTotalcg';
            case 'AG':
                return 'https://twitter.com/aribagp';
            case 'AK':
                return 'https://twitter.com/Account_Kings';
            default:
                return UNDEFINED_SHORTCODE;
        }
    }

    get socialMediaFacebook() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.facebook.com/Chicksgroup';
            case 'CX':
                return 'https://www.facebook.com/chicksxcrypto';
            case 'DS':
                return 'https://www.facebook.com/Chicksgroup';
            case 'GT':
                return 'https://www.facebook.com/gamertotalcg';
            case 'AG':
                return 'https://www.facebook.com/aribagp';
            case 'AK':
                return 'https://www.facebook.com/acckingsofficial';
            default:
                return UNDEFINED_SHORTCODE;
        }
    }

    get socialMediaInstagram() {
        switch (this.shortCode) {
            case 'CG':
                return 'https://www.instagram.com/chicksgold/';
            case 'CX':
                return 'https://www.instagram.com/chicksxcrypto/';
            case 'DS':
                return 'https://www.instagram.com/divicasales/';
            case 'GT':
                return 'https://www.instagram.com/gamertotalcg/';
            case 'AG':
                return 'https://www.instagram.com/aribagoldd/';
            case 'AK':
                return 'https://www.instagram.com/acc_kings/';
            default:
                return UNDEFINED_SHORTCODE;
        }
    }

    get socialMediaDiscord() {
        switch (this.shortCode) {
            case 'CG':
                return DISCORD_INVITE_LINK;
            case 'CX':
                return 'https://discord.com/invite/chicksx';
            case 'DS':
                return DISCORD_INVITE_LINK;
            case 'GT':
                return DISCORD_INVITE_LINK;
            case 'AG':
                return DISCORD_INVITE_LINK;
            case 'AK':
                return DISCORD_INVITE_LINK;
            default:
                return UNDEFINED_SHORTCODE;
        }
    }

    get currencyStorage(): string | null {
        return localStorage.getItem('currency');
    }

    get languageStorage(): string | null {
        const language = localStorage.getItem('language');
        if (!language) return null;
        const languageJSON = JSON.parse(language);
        return languageJSON?.language?.name ?? null;
    }

    handleComponentEvent = async (evt: CustomEvent<LanguageCurrencyChangedDetail>) => {
        if (evt.type !== 'languagecurrencychanged') return;
        const { type, value } = evt.detail;

        if (type === 'currency') {
            window.localStorage[CURRENCY_KEY] = value;
        } else if (type === 'language') {
            const prevLanguage = this.selectedLanguage;
            const languagesByWebsite: LanguageItem[] = await this.languageService.getByWebsite();
            const selected = languagesByWebsite.find(
                (lang: LanguageItem) => lang.language?.id === value || lang.languageId === value
            );

            if (selected) {
                this.selectedLanguage = {
                    languageId: selected.languageId,
                    language: {
                        name: selected.language.name,
                        hrefLang: selected.language.hrefLang
                    }
                };
                this.sessionService.saveLanguage(this.selectedLanguage);
            }

            this.handleLanguageForHtml();
            this.changeLanguageURL(prevLanguage, this.selectedLanguage, languagesByWebsite);
        }
    };

    handleLanguageForHtml() {
        if (this.selectedLanguage) {
            const htmlElement = document.querySelector('html');
            if (htmlElement && this.selectedLanguage.language) {
                htmlElement.setAttribute('lang', this.selectedLanguage.language.hrefLang);
            }
        }
    }

    changeLanguageURL(prevLanguage: Language | null, selectedLanguage: Language | null, languages: LanguageItem[] = []) {
        const extractHrefLang = (language: Language | null): string => language?.language?.hrefLang ?? '';

        const previousHrefLang = extractHrefLang(prevLanguage);
        const selectedHrefLang = extractHrefLang(selectedLanguage);

        if (!selectedHrefLang) return;

        const englishCodes = new Set(['en', 'en-us']);
        const normalizedSelected = selectedHrefLang.toLowerCase();
        const normalizedPrevious = previousHrefLang.toLowerCase();

        const pathSegments = window.location.pathname.split('/').filter(Boolean);

        const knownLanguageCodes = new Set<string>();
        languages.forEach((lang: LanguageItem) => {
            const hrefLang = lang?.language?.hrefLang;
            if (hrefLang && typeof hrefLang === 'string') {
                knownLanguageCodes.add(hrefLang.toLowerCase());
            }
        });
        if (normalizedPrevious) knownLanguageCodes.add(normalizedPrevious);
        if (normalizedSelected) knownLanguageCodes.add(normalizedSelected);

        const firstSegment = pathSegments[0]?.toLowerCase();
        if (firstSegment && knownLanguageCodes.has(firstSegment)) {
            pathSegments.shift();
        }

        const shouldPrefix = normalizedSelected && !englishCodes.has(normalizedSelected);
        const newPathSegments = shouldPrefix ? [selectedHrefLang, ...pathSegments] : pathSegments;
        const newPathname = `/${newPathSegments.join('/')}`;

        const search = window.location.search || '';
        const hash = window.location.hash || '';
        const newUrl = `${window.location.origin}${newPathname}${search}${hash}`;
        const currentUrl = `${window.location.origin}${window.location.pathname}${search}${hash}`;

        if (newUrl !== currentUrl) {
            window.location.href = newUrl;
        }
    }
}
